//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { createContext, useContext } from 'react';
import styled, { css } from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { SectionText, SubtitleText } from '@components/atoms/text/Text';
import { Dialog } from '@components/primitives/dialog/Dialog';
import { TrackingIdPrefix } from '@models/TrackingId';
import { isDefined } from '@utils/filterPredicates';
import { withTracking } from '@utils/withTracking';

interface TrackingContext {
  trackingIdPrefix?: TrackingIdPrefix;
}

const DrawerTrackingContext = createContext<TrackingContext>({
  trackingIdPrefix: undefined,
});

export type RightDrawerWidth = 'medium' | 'wide' | 'extra-wide';

interface RightDrawerProps {
  children: React.ReactNode;
  className?: string;
  closeDrawer: (() => void) | undefined;
  contentOnKeyDown?: React.ComponentPropsWithoutRef<
    typeof Content
  >['onKeyDown'];
  contentRef?: React.ComponentProps<typeof Content>['ref'];
  isDrawerOpen: boolean;
  shouldCloseOnClickOutside?: boolean;
  shouldCloseOnEsc?: boolean;
  shouldHideOverlay?: boolean;
  trackingIdPrefix?: TrackingIdPrefix;
  width?: RightDrawerWidth;
}

function getDrawerWidth(width: RightDrawerWidth) {
  if (width === 'medium') {
    return theme.mediumMeasure;
  }

  if (width === 'extra-wide') {
    return theme.extraWideMeasure;
  }

  // Default to 'wide'
  return theme.wideMeasure;
}

const RightDrawerComponent = ({
  children,
  className,
  closeDrawer,
  contentOnKeyDown,
  contentRef,
  isDrawerOpen,
  shouldCloseOnClickOutside = true,
  shouldCloseOnEsc = true,
  shouldHideOverlay = false,
  trackingIdPrefix,
  width = 'medium',
}: RightDrawerProps) => {
  return (
    <DrawerTrackingContext.Provider value={{ trackingIdPrefix }}>
      <Dialog
        onOpenChange={(value) => !value && closeDrawer?.()}
        open={isDrawerOpen}
      >
        <Content
          $shouldHideOverlay={shouldHideOverlay}
          $width={width}
          className={className}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            shouldCloseOnEsc && closeDrawer?.();
          }}
          onKeyDown={contentOnKeyDown}
          onPointerDownOutside={(event) => {
            event.preventDefault();
            shouldCloseOnClickOutside && closeDrawer?.();
          }}
          ref={contentRef}
          trackingId={
            isDefined(trackingIdPrefix)
              ? `${trackingIdPrefix}content`
              : undefined
          }
        >
          {children}
        </Content>
      </Dialog>
    </DrawerTrackingContext.Provider>
  );
};

const Content = withTracking(
  styled(Dialog.Content)<{
    $shouldHideOverlay?: boolean;
    $width: RightDrawerWidth;
  }>(
    ({ $width, theme }) => css`
      background: ${theme.colors.white};
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      bottom: 0;
      left: unset;
      right: 0;
      top: 0;
      transform: unset;
      width: ${getDrawerWidth($width)};
    `,
  ),
);

const Header = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: ${theme.spacings[4]};
    border-bottom: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
    flex: none;
  `,
);

const HeaderTitleGroup = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
  `,
);

const HeaderTitle = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <Dialog.Title asChild>
      <h1>
        <SubtitleText className={className}>{children}</SubtitleText>
      </h1>
    </Dialog.Title>
  );
};

const HeaderDescription = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <Dialog.Description asChild>
      <p>
        <SectionText className={className}>{children}</SectionText>
      </p>
    </Dialog.Description>
  );
};

const HeaderClose = ({
  className,
  isDisabled,
  onClick,
}: {
  className?: string;
  isDisabled?: boolean;
  onClick: () => void;
}) => {
  const { trackingIdPrefix } = useContext(DrawerTrackingContext);

  return (
    <Dialog.Close asChild className={className}>
      <InvisibleButton
        aria-label='Close drawer'
        isDisabled={isDisabled}
        onClick={onClick}
        trackingId={
          isDefined(trackingIdPrefix)
            ? `${trackingIdPrefix}close-button`
            : undefined
        }
      >
        <Icon name='close' />
      </InvisibleButton>
    </Dialog.Close>
  );
};

const Body = styled.div(
  ({ theme }) => `
    flex: 1 1 auto;
    padding: ${theme.spacings[3]} ${theme.spacings[4]} ;
    overflow-y: auto;
  `,
);

const Footer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
  `,
);

const FooterActionBar = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: flex-end;
    gap: ${theme.spacings[3]};
  `,
);

RightDrawerComponent.Header = Header;
RightDrawerComponent.HeaderTitleGroup = HeaderTitleGroup;
RightDrawerComponent.HeaderTitle = HeaderTitle;
RightDrawerComponent.HeaderDescription = HeaderDescription;
RightDrawerComponent.HeaderClose = HeaderClose;
RightDrawerComponent.Body = Body;
RightDrawerComponent.Footer = Footer;
RightDrawerComponent.FooterActionBar = FooterActionBar;

/**
 * A right-side drawer component that slides in from the right side of the screen.
 * This component uses a composable approach allowing for flexible layout composition.
 *
 * The `RightDrawer` component is designed to provide a customizable and accessible
 * drawer interface. It supports various configurations such as width, overlay visibility,
 * and behavior on outside clicks or escape key presses.
 *
 * ```tsx
 * <RightDrawer
 *   isDrawerOpen={isOpen}
 *   closeDrawer={handleClose}
 *   width="wide"
 * >
 *   <RightDrawer.Header>
 *     <RightDrawer.HeaderTitleGroup>
 *       <RightDrawer.HeaderTitle>Title</RightDrawer.HeaderTitle>
 *       <RightDrawer.HeaderDescription>Description</RightDrawer.HeaderDescription>
 *     </RightDrawer.HeaderTitleGroup>
 *     <RightDrawer.HeaderClose onClick={handleClose} />
 *   </RightDrawer.Header>
 *   <RightDrawer.Body>Content</RightDrawer.Body>
 *   <RightDrawer.Footer>
 *     <RightDrawer.FooterActionBar>
 *       <Button>Cancel</Button>
 *       <Button>Save</Button>
 *     </RightDrawer.FooterActionBar>
 *   </RightDrawer.Footer>
 * </RightDrawer>
 * ```
 */
export const RightDrawer = RightDrawerComponent;
